import { createContext, FC, useContext, useEffect, useState } from "react";
import { VehicleTripFiltersData } from "../../map-asset/modalVehicleTripFilters";
import { APP_CONFIG } from "../../../constants/constants";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  getStartEndOfDayInUTC,
  jsTimeToDBTime,
} from "../../../utils/dateTime";
import { useAccountTimezone } from "../../../hooks/timezone";
import { useDispatch, useSelector } from "react-redux";
import {
  setListingQueryParams,
  TListingQueryParams,
} from "../../../api/store/commonSlice";

// inspo from map-asset.tsx for
const defaultTimeFilters = window.location.hostname.includes("localhost")
  ? {
      selected_date: "2024-04-22",
      date_from: "2024-04-22",
      time_from: "00:00:00",
      date_to: "2024-04-22",
      time_to: "23:59:59",
      timezone: "US/Arizona",
      calculatedUnixTimestampFrom: 1713769200,
      calculatedUnixTimestampTo: 1713855599,
    }
  : ({
      selected_date: "",
      date_from: "",
      date_to: "",
      time_from: "",
      time_to: "",
      timezone: APP_CONFIG.DEFAULT_TIMEZONE,
      calculatedUnixTimestampFrom: null,
      calculatedUnixTimestampTo: null,
    } as VehicleTripFiltersData);

const contextDefaultValue = {
  timeFilters: defaultTimeFilters,
  setTimeFilters: (timeFilters: VehicleTripFiltersData) => {},
};

const crashtrackContext = createContext(contextDefaultValue);

export const CrashtrackContextProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { crashTrack: crashTrackQueryParams } = listingQueryParams;

  // retrieve account timezone
  const { tzForDateFns } = useAccountTimezone();

  const [timeFilters, setTimeFilters] =
    useState<VehicleTripFiltersData>(defaultTimeFilters);

  useEffect(() => {
    if (timeFilters.calculatedUnixTimestampFrom !== null) return;
    const refTimestamp = new Date().getTime();
    const refDateStringInTimezone = formattedDateTimeBasedOnTimezone(
      refTimestamp,
      tzForDateFns || APP_CONFIG.DEFAULT_TIMEZONE,
      "yyyy-MM-dd"
    );
    if (APP_CONFIG.DEBUG.ALL)
      console.log("ASSETFILT refDateStringInTimezone", refDateStringInTimezone);

    const { startOfDayInUTC, endOfDayInUTC } = getStartEndOfDayInUTC(
      refDateStringInTimezone,
      tzForDateFns || APP_CONFIG.DEFAULT_TIMEZONE
    );

    const unixTimestampFrom = jsTimeToDBTime(startOfDayInUTC.getTime());
    const unixTimestampTo = jsTimeToDBTime(endOfDayInUTC.getTime());
    if (APP_CONFIG.DEBUG.ALL)
      console.log(
        "ASSETFILT >>>>>",
        dbTimeToJsTime(parseInt(`${unixTimestampFrom}`))
      );
    setTimeFilters({
      ...timeFilters,
      selected_date: new Date(dbTimeToJsTime(parseInt(`${unixTimestampFrom}`)))
        .toISOString()
        .split("T")[0],
      date_from: formattedDateTimeBasedOnTimezone(
        dbTimeToJsTime(parseInt(`${unixTimestampFrom}`)),
        tzForDateFns || APP_CONFIG.DEFAULT_TIMEZONE,
        "yyyy-MM-dd"
      ),
      date_to: formattedDateTimeBasedOnTimezone(
        dbTimeToJsTime(parseInt(`${unixTimestampTo}`)),
        tzForDateFns || APP_CONFIG.DEFAULT_TIMEZONE,
        "yyyy-MM-dd"
      ),
      time_from: formattedDateTimeBasedOnTimezone(
        dbTimeToJsTime(parseInt(`${unixTimestampFrom}`)),
        tzForDateFns || APP_CONFIG.DEFAULT_TIMEZONE,
        "HH:mm:ss"
      ),
      time_to: formattedDateTimeBasedOnTimezone(
        dbTimeToJsTime(parseInt(`${unixTimestampTo}`)),
        tzForDateFns || APP_CONFIG.DEFAULT_TIMEZONE,
        "HH:mm:ss"
      ),
      calculatedUnixTimestampFrom: unixTimestampFrom,
      calculatedUnixTimestampTo: unixTimestampTo,
    });
  }, []);

  useEffect(() => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        crashTrack: {
          ...crashTrackQueryParams,
          page: 1,
          start_time: timeFilters.calculatedUnixTimestampFrom,
          end_time: timeFilters.calculatedUnixTimestampTo,
        },
      })
    );
  }, [timeFilters]);

  return (
    <crashtrackContext.Provider
      value={{
        timeFilters,
        setTimeFilters,
      }}
    >
      {children}
    </crashtrackContext.Provider>
  );
};

export const useCrashtrackContext = () => {
  const { timeFilters, setTimeFilters } = useContext(crashtrackContext);
  return {
    timeFilters,
    setTimeFilters,
  };
};
